declare global {
  interface Window {
    MathJax: any
  }
}

export const getContentString = (
  elements: any[],
  pageWidth: number,
  options: {
    imageRenderMode?: "contain" | "cover" | "auto-size" | "original"
    minimizeWhitespace?: boolean
  } = {}
): string => {
  const { imageRenderMode = "contain", minimizeWhitespace = true } = options
  // Sort elements by page and vertical position to ensure proper ordering
  const sortedElements = elements?.slice().sort((a, b) => {
    const aPage = a.page_count || 1
    const bPage = b.page_count || 1
    if (aPage !== bPage) return aPage - bPage

    const aY = (a.fix_width_bbox || a.bbox)?.[1] || 0
    const bY = (b.fix_width_bbox || b.bbox)?.[1] || 0
    return aY - bY
  })

  // Calculate dynamic page height based on content
  const pageHeight = 1200 // Increased from 1000 for better spacing
  let totalHeight = 0
  let lastElementBottom = 0

  // Function to calculate ultra-compact spacing between elements
  const calculateSpacing = (
    currentEl: any,
    previousEl: any,
    currentTop: number
  ) => {
    if (!previousEl) return currentTop

    const prevBottom = lastElementBottom

    // Ultra-compact spacing
    let optimalGap

    if (previousEl.type === "graph" || previousEl.type === "figure") {
      // After images, use very small gap
      optimalGap = currentEl.type === "text" ? 12 : 8
    } else {
      // Between text elements
      optimalGap = currentEl.type === "text" ? 10 : 6
    }

    // Always use the optimal gap, ignore OCR spacing
    return prevBottom + optimalGap
  }

  let contentString = `
    <div style="position: relative; width: 100%; height: fit-content; padding-bottom: 20px; overflow: visible;">
      <div style="display: flex; flex-direction: column; width: 100%; background-color: #ffffff;">
  `

  const wrapLatexInMathML = (content: string): string => {
    if (!content) return ""

    // Clean up content and preserve line breaks
    content = content.replace(/\r\n|\r/g, "\n")
    content = content.replace(/\n/g, "<br>")

    // Handle mathematical expressions with better regex
    content = content.replace(/\$\$([^$]+)\$\$/g, "\\[$1\\]") // Convert $$ to \[ \]
    content = content.replace(/\$([^$]+)\$/g, "\\($1\\)") // Convert $ to \( \)

    return content.replace(/(\\\[.*?\\\]|\\\(.*?\\\))/g, (match) => {
      try {
        const latex = match.replace(/\\\[|\\\]|\\\(|\\\)/g, "")
        if (window.MathJax && window.MathJax.tex2mml) {
          const isDisplayMode = match.startsWith("\\[")
          return window.MathJax.tex2mml(latex, { display: isDisplayMode })
        }
        return match
      } catch (err) {
        console.error("MathJax LaTeX → MathML conversion failed", err)
        return `<span style="background-color: #ffe6e6; padding: 2px; border-radius: 2px;">${match}</span>`
      }
    })
  }

  sortedElements?.forEach((el: any, index: number) => {
    if (!el?.bbox) return

    const [xMin, yMin, xMax, yMax] = el.fix_width_bbox || el.bbox
    const width = Math.max((xMax - xMin) * pageWidth, 50) // Minimum width
    
    // Calculate margins
    const marginLeft = Math.max(xMin * pageWidth, 0)
    
    // Calculate top margin (gap)
    let marginTop = 0
    if (index === 0) {
      // For the first element, preserve the initial top offset relative to the page
      marginTop = Math.max(yMin * pageHeight, 0)
    } else {
      const previousEl = sortedElements[index - 1]
      // Determine gap based on element types
      if (previousEl.type === "graph" || previousEl.type === "figure") {
        marginTop = el.type === "text" ? 12 : 8
      } else {
        marginTop = el.type === "text" ? 10 : 6
      }
    }

    if (el.type === "text") {
      const processedContent = wrapLatexInMathML(el.content)
      contentString += `
        <div id="content-${index}" style="
          position: relative;
          margin-left: ${marginLeft}px;
          margin-top: ${marginTop}px;
          width: ${width}px;
          padding: 1px 2px;
          box-sizing: border-box;
          overflow: visible;
          z-index: ${index};
        ">
          <div style="
            margin: 0; 
            font-size: 11px; 
            line-height: 1.3;
            word-wrap: break-word;
            overflow-wrap: break-word;
            font-family: 'Arial', sans-serif;
            color: #333;
          ">
            ${processedContent}
          </div>
        </div>
      `
    } else if (el.type === "graph" || el.type === "figure") {
      const height = Math.max((yMax - yMin) * pageHeight, 20) // Minimum height for images
      
      // Dynamic image rendering based on mode
      let imageStyles = ""
      let containerStyles = ""

      switch (imageRenderMode) {
        case "cover":
          containerStyles = `
            width: ${width}px; 
            height: ${height}px; 
            overflow: hidden;
          `
          imageStyles = `
            width: 100%; 
            height: 100%; 
            object-fit: cover;
          `
          break

        case "auto-size":
          containerStyles = `
            max-width: ${width}px; 
            max-height: ${height}px;
            display: inline-block;
          `
          imageStyles = `
            max-width: 100%; 
            height: auto;
          `
          break

        case "original":
          containerStyles = `
            width: auto;
            height: auto;
          `
          imageStyles = `
            max-width: ${width}px;
            max-height: ${height}px;
          `
          break

        case "contain":
        default:
          containerStyles = `
            width: ${width}px; 
            height: ${height}px; 
            display: flex;
            align-items: center;
            justify-content: center;
          `
          imageStyles = `
            max-width: 100%; 
            max-height: 100%; 
            object-fit: contain;
          `
          break
      }

      const borderStyle = minimizeWhitespace ? "" : "border: 1px solid #e5e7eb;"

      contentString += `
        <div id="content-${index}" style="
          position: relative;
          margin-left: ${marginLeft}px;
          margin-top: ${marginTop}px;
          z-index: ${index};
          background-color: #ffffff;
          border-radius: 4px;
          ${borderStyle}
          ${containerStyles}
        ">
          <img src="${el.content}" alt="Visualization ${index}" style="
            display: block;
            margin: 0;
            padding: 0;
            ${imageStyles}
          " />
        </div>
      `
    }
  })

  contentString += "</div></div>"
  return contentString
}
